import { useState, useRef, FC, useEffect, KeyboardEvent } from 'react';
import { UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { HeroFormValues } from '~js/React/types/interfaces';
import useMediaQuery from '../../CustomHooks/useMediaQuery';

type Props = {
  name: 'estateType' | 'roomsMin';
  options?: string[];
  setValue: UseFormSetValue<HeroFormValues>;
  getValues: UseFormGetValues<HeroFormValues>;
  labelText: string;
  placeholderText: string;
  icon?: string;
  psuedo?: boolean;
};

const DropDown: FC<Props> = ({
  name,
  options,
  setValue,
  getValues,
  labelText,
  placeholderText,
  icon,
  psuedo,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const dropDownRef = useRef<HTMLDivElement>(null);
  const isMediumScreen = useMediaQuery('(min-width: 64rem)');
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const selectOption = (option: string) => {
    setOpen(false);
    setValue(name, option);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropDownRef.current &&
      !dropDownRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

   const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(prev => !prev);
    } else if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleOptionKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectOption(options[index]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(Math.min(index + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index > 0) {
        setActiveIndex(index - 1);
      } else {
        dropDownRef.current?.querySelector('button')?.focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      dropDownRef.current?.querySelector('button')?.focus();
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && open) {
      const options = dropDownRef.current?.querySelectorAll('[role="option"]');
      if (options?.[activeIndex]) {
        (options[activeIndex] as HTMLElement).focus();
      }
    }
  }, [activeIndex, open]);

  return (
    <div
      ref={dropDownRef}
      className={`relative ${
        psuedo
          ? "lg:before:content-[''] lg:before:w-[1px] lg:before:h-5/6 lg:before:bg-grey100 lg:before:absolute lg:before:-left-[18px] lg:before:top-1/2 lg:before:-translate-y-1/2"
          : ''
      }`}
    >
      <div className="flex flex-row items-center gap-2 lg:gap-4 bg-white lg:shadow-none px-4 lg:px-2 border border-none rounded-3xl w-full">
        <img className="w-5.5 h-auto" src={icon} alt="" aria-hidden="true" />
        <div className="w-full lg:w-auto">
          <label
            id={`${name}-label`}
            className="lg:block hidden font-icb text-blue700 text-sm whitespace-nowrap"
          >
            {labelText}
          </label>
          <button
            id={name}
            aria-labelledby={`${name}-label`} 
            aria-haspopup="listbox" 
            aria-expanded={open}
            aria-controls={open ? `${name}-listbox` : undefined}
            className="flex justify-between lg:justify-normal items-center lg:gap-4 w-full text-left cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setOpen((prevValue) => !prevValue);
            }}
            onKeyDown={handleKeyDown}
          >
            <span
              className="px-1 lg:px-0 py-[11px] lg:py-1 w-full lg:w-fit font-ic text-base lg:text-black500 whitespace-nowrap"
            >
              {getValues(name) ? (
                getValues(name)
              ) : (
                <span className="font-ic text-grey400 whitespace-nowrap">
                  {isMediumScreen ? placeholderText : labelText}
                </span>
              )}
            </span>
            <img
              className={`w-[10px] h-auto transition-all duration-75 ${
                open ? 'rotate-180' : ''
              }`}
              src="/Images/Icons/Arrows/arrow-down-simple-black500.svg"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      {open && (
        <div 
          id={`${name}-listbox`}
          role="listbox"
          aria-labelledby={`${name}-label`}
          className="top-12 lg:top-[66px] left-1/2 z-10 absolute flex flex-col bg-grey50 shadow-bs80 rounded-2xl w-[92%] lg:w-fit lg:min-w-[180px] max-h-s -translate-x-1/2 overflow-x-hidden overflow-y-auto"
        >
          {options.map((option, index) => (
            <button
              role="option"
              aria-selected={getValues(name) === option}
              tabIndex={activeIndex === index ? 0 : -1} 
              className="flex justify-between border-grey100 hover:bg-blue100 px-4 py-2 border-b w-full font-ic cursor-pointer outline-none focus-visible:bg-blue100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue400 focus-visible:-outline-offset-2"
              key={option}
              onClick={() => selectOption(option)}
              onKeyDown={(e) => handleOptionKeyDown(e, index)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropDown;
